import { FC, PropsWithChildren, useEffect, useState } from "react";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
type Item = {
  order: number;
  id: number;
  parentId: number | null;
  name: string;
};

const SortComponent: FC<
  PropsWithChildren<{
    items: any[];
    loading?: boolean;
    onChange?: (params: { newOrder: number; oldOrder: number }) => void;
    onSelect?: (params: any) => void;
  }>
> = ({ items, children, onChange, onSelect, loading }) => {
  const [allItems, setAllItems] = useState<Item[]>([]);
  const sensors = useSensors(useSensor(PointerSensor));

  const SortableItem = ({
    item,
    onClick,
  }: {
    item: any;
    onClick?: (params: any) => void;
  }) => {
    const {
      attributes,
      listeners,
      setNodeRef,
      transform,
      transition,
      isDragging,
    } = useSortable({ id: item.id }); // must be unique

    const style = {
      transform:
        CSS.Transform.toString(transform),
      transition,
      zIndex: isDragging ? 999 : "auto",
      boxShadow: isDragging
        ? "0 10px 25px rgba(0,0,0,0.2)"
        : "0 1px 3px rgba(0,0,0,0.1)",
      opacity: isDragging ? 0.95 : 1,
      scale: isDragging ? 1.05 : 1
    };

    return (
      <div
        ref={setNodeRef}
        style={style}
        {...attributes}
        // keep attributes on container
        className={isDragging ? "flex items-center shadow-md  my-2 rounded w-full border bg-gray-50" :"flex items-center shadow-md  my-2 rounded w-full border hover:bg-gray-200"}
      >
        <button
          disabled={loading}
          onClick={() => {
            if (onClick) onClick(item);
          }}
          className={
            loading
              ? "flex-1 text-left cursor-progress py-5 px-4"
              : "flex-1 text-left cursor-pointer py-5 px-4"
          }
        >
          {item.name}
        </button>
        <span
          {...listeners} // listeners only on handle
          className={
            loading
              ? "cursor-progress py-2 me-3 text-gray-500 w-fit rounded-md"
              : isDragging
                ? "cursor-grabbing py-3 px-1 me-3 text-gray-500 hover:text-gray-700 w-fit bg-gray-200 rounded-md shadow-md"
                : "cursor-grab py-3 px-1 me-3 text-gray-500 hover:text-gray-700 w-fit hover:bg-gray-200 rounded-md"
          }
        >
          <DragIndicatorIcon />
        </span>
      </div>
    );
  };

  const handleDragEnd = (event: any) => {
    if (!loading) {
      const { active, over } = event;
      if (!over || active.id === over.id) return;
      const allItemsCopy = [...allItems];
      const oldIndex = allItemsCopy.findIndex((item) => item.id === active.id);
      const newIndex = allItemsCopy.findIndex((item) => item.id === over.id);
      const order = {
        newOrder: allItemsCopy[newIndex]?.order || 0,
        oldOrder: allItemsCopy[oldIndex]?.order || 0,
      };
      allItemsCopy[newIndex] = {
        ...allItemsCopy[newIndex],
        order: order.oldOrder,
      };
      allItemsCopy[oldIndex] = {
        ...allItemsCopy[oldIndex],
        order: order.newOrder,
      };
      const newArray = arrayMove(allItemsCopy, oldIndex, newIndex);
      if (onChange) {
        onChange(order);
      }
      setAllItems(newArray);
    }
  };

  useEffect(() => {
    setAllItems(items);
  }, [items]);

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
    >
      <SortableContext
        items={allItems.map((item) => item.id)} // must be array of IDs
        strategy={verticalListSortingStrategy}
      >
        {allItems.map((item) =>
          children ? (
            children
          ) : (
            <SortableItem key={item.id} item={item} onClick={onSelect} />
          )
        )}
      </SortableContext>
    </DndContext>
  );
};

export default SortComponent;
